// TelcoTrack — Typ OK derived from optical-cable material (SAP / product category / name).
// SSOT for záfuk: user picks catalog material; typ OK is not a separate required picker.
// Catalog path: Material_k_objektum/BUDOVA.csv → Záfuk / Kabely / `{N} vl.`

import { isAnyTypOkValue, type RozvadecTypOkValue, type TypOkValue } from './typ-ok.constants';

export type DerivedTypOkValue = TypOkValue | RozvadecTypOkValue | '1 vl.' | '72 vl.' | '288 vl.';

/** Catalog fiber counts that map to a persisted Typ OK label. */
export const OK_CATALOG_FIBER_COUNTS = [1, 2, 6, 12, 24, 48, 72, 96, 144, 192, 288] as const;

export type OkCatalogFiberCount = (typeof OK_CATALOG_FIBER_COUNTS)[number];

export const OK_MATERIAL_REQUIRED_MESSAGE = 'Zvolte alespoň jeden OK kabel v materiálu.';

/**
 * SAP → Typ OK from recommended Záfuk / Kabely catalog.
 * One SAP never maps to two types.
 */
export const OK_MATERIAL_SAP_TO_TYP_OK: Readonly<Record<string, DerivedTypOkValue>> = Object.freeze({
  '325244': '1 vl.',
  '325604': '2 vl.',
  '325615': '2 vl.',
  '317636': '2 vl.',
  '325245': '2 vl.',
  '325605': '6 vl.',
  '325616': '6 vl.',
  '325231': '6 vl.',
  '325606': '12 vl.',
  '325617': '12 vl.',
  '319711': '12 vl.',
  '325232': '12 vl.',
  '300675': '12 vl.',
  '300218': '24 vl.',
  '319712': '24 vl.',
  '323035': '24 vl.',
  '323551': '24 vl.',
  '324274': '24 vl.',
  '324908': '24 vl.',
  '325607': '48 vl.',
  '300726': '48 vl.',
  '323192': '48 vl.',
  '319696': '72 vl.',
  '316970': '96 vl.',
  '316971': '96 vl.',
  '317327': '96 vl.',
  '325608': '144 vl.',
  '300718': '144 vl.',
  '326779': '144 vl.',
  '300719': '144 vl.',
  '325612': '192 vl.',
  '300716': '288 vl.',
});

export type OkMaterialUsageLike = {
  sapCode?: string | null;
  materialCode?: string | null;
  name?: string | null;
  label?: string | null;
  quantity?: number | null;
  productCategoryPath?: readonly string[] | null;
};

export type TypOkFromMaterialsResult =
  | { status: 'none'; types: readonly DerivedTypOkValue[] }
  | { status: 'resolved'; typOk: DerivedTypOkValue; types: readonly DerivedTypOkValue[] };

function normalizeSap(raw: string | null | undefined): string {
  return (raw ?? '').trim();
}

function usageHasQuantity(used: OkMaterialUsageLike): boolean {
  if (used.quantity == null) return true;
  return Number.isFinite(used.quantity) && used.quantity > 0;
}

export function typOkLabelFromFiberCount(fiberCount: number): DerivedTypOkValue | null {
  if (!Number.isInteger(fiberCount) || fiberCount <= 0) return null;
  if (!(OK_CATALOG_FIBER_COUNTS as readonly number[]).includes(fiberCount)) return null;
  return `${fiberCount} vl.` as DerivedTypOkValue;
}

/**
 * Product-category leaf such as `12 vl.` / `12vl` / `12 vláken` → canonical Typ OK.
 */
export function typOkFromCategorySegment(raw: string | null | undefined): DerivedTypOkValue | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  if (isDerivedTypOkValue(text)) return text;
  const match = text.match(/^(\d+)\s*(?:vl\.?|vláken|f)\b/i);
  if (!match) return null;
  return typOkLabelFromFiberCount(Number.parseInt(match[1], 10));
}

export function isDerivedTypOkValue(value: string | null | undefined): value is DerivedTypOkValue {
  const raw = (value ?? '').trim();
  if (isAnyTypOkValue(raw)) return true;
  return raw === '1 vl.' || raw === '72 vl.' || raw === '288 vl.';
}

export function typOkFromProductCategoryPath(
  path: readonly string[] | null | undefined,
): DerivedTypOkValue | null {
  if (!path?.length) return null;
  for (let i = path.length - 1; i >= 0; i--) {
    const fromSeg = typOkFromCategorySegment(path[i]);
    if (fromSeg) return fromSeg;
  }
  return null;
}

/**
 * Parse fiber count from catalog / SAP names (`Mikrokabel 48 vl.`, `12f`, `24vl.`).
 * Prefers an explicit `{N} vl.` token over a bare `Nf` so OD=1,6 is not read as 6 fibers.
 */
export function typOkFromMaterialName(raw: string | null | undefined): DerivedTypOkValue | null {
  const text = (raw ?? '').trim();
  if (!text) return null;
  const vlMatch = text.match(/(\d+)\s*(?:vl\.?|vláken)\b/i);
  if (vlMatch) return typOkLabelFromFiberCount(Number.parseInt(vlMatch[1], 10));
  const fMatch = text.match(/(\d+)\s*f\b/i);
  if (fMatch) return typOkLabelFromFiberCount(Number.parseInt(fMatch[1], 10));
  return null;
}

export function typOkFromSapCode(sapCode: string | null | undefined): DerivedTypOkValue | null {
  const sap = normalizeSap(sapCode);
  if (!sap) return null;
  return OK_MATERIAL_SAP_TO_TYP_OK[sap] ?? null;
}

/** SAP first, then product category, then name. */
export function typOkFromMaterial(used: OkMaterialUsageLike | null | undefined): DerivedTypOkValue | null {
  if (!used || !usageHasQuantity(used)) return null;
  const sap = typOkFromSapCode(used.sapCode) ?? typOkFromSapCode(used.materialCode);
  if (sap) return sap;
  const fromPath = typOkFromProductCategoryPath(used.productCategoryPath);
  if (fromPath) return fromPath;
  return typOkFromMaterialName(used.name) ?? typOkFromMaterialName(used.label);
}

export function collectTypOkFromMaterials(
  used: readonly OkMaterialUsageLike[] | null | undefined,
): DerivedTypOkValue[] {
  if (!used?.length) return [];
  const out: DerivedTypOkValue[] = [];
  for (const row of used) {
    const typOk = typOkFromMaterial(row);
    if (typOk) out.push(typOk);
  }
  return out;
}

export function resolveTypOkFromMaterials(
  used: readonly OkMaterialUsageLike[] | null | undefined,
): TypOkFromMaterialsResult {
  const types = collectTypOkFromMaterials(used);
  if (types.length === 0) return { status: 'none', types };
  return { status: 'resolved', typOk: types[0], types };
}

export function hasRequiredOkCableMaterial(
  used: readonly OkMaterialUsageLike[] | null | undefined,
): boolean {
  return resolveTypOkFromMaterials(used).status === 'resolved';
}

/**
 * BUDOVA záfuk gate: an OK cable in materiál is required.
 * Persisted typ OK (HP prefill / leftover column) must not satisfy this.
 * Výchozí bod is a ROZVADEC product skip — callers for budova pass false.
 */
export function isBudovaZafukOkMaterialReady(
  vychoziBod: boolean,
  materials: readonly OkMaterialUsageLike[] | null | undefined,
): boolean {
  if (vychoziBod) return true;
  return hasRequiredOkCableMaterial(materials);
}

export function applyDerivedTypOkList(
  opticalCount: number,
  types: readonly DerivedTypOkValue[],
): Array<DerivedTypOkValue | null> {
  if (opticalCount <= 0) return [];
  if (types.length === 0) return Array.from({ length: opticalCount }, () => null);
  return Array.from({ length: opticalCount }, (_, i) => types[Math.min(i, types.length - 1)] ?? types[0]);
}
